import {
  buildProjectionSegments,
  MAX_GRID_SEGMENTS,
} from '@modules/editor/src/plugins/grid/utils/projection';
import { ensureLoadedImage } from '@modules/editor/src/utils/image';
import { type Point } from '@modules/editor/src/utils/math';
import {
  getPerspectiveTransform,
  INVERSE_BILINEAR_GLSL,
} from '@modules/editor/src/utils/perspective';
import * as twgl from 'twgl.js';

import {
  getCurrentView,
  getVisibleComponentIds,
  type SelectorState,
} from '$/pages/InspirationEditorPage/lib/selectors';
import { type DetailedInspiration } from '$/services/usecases/inspirations/mapper/inspiration.schema';
import { hexToRgb } from '$/utils/colorUtils';

export type Mapping = {
  mask: string;
  material: string | undefined;
  is_texture_material: boolean;
  corners: Point[];
  cornerLines?: Point[][];
  object_width: number;
  object_height: number;
  rotation: number;
  real_texture_width: number;
  real_texture_height: number;
};

type MaterialEntry = {
  texture: WebGLTexture;
  width: number;
  height: number;
};

type SceneTextures = {
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
  layerTextures: Map<string, WebGLTexture>;
  maskTextures: Map<string, WebGLTexture>;
  maskData: Map<string, Uint8ClampedArray>;
};

type State = {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  materialCache: Map<string, MaterialEntry | Promise<MaterialEntry | null>>;
  diffuseFramebuffer: WebGLFramebuffer;
  diffuseTexture: WebGLTexture;
  blitProgram: twgl.ProgramInfo;
  materialProgram: twgl.ProgramInfo;
  compositeProgram: twgl.ProgramInfo;
  fullScreenBuffer: twgl.BufferInfo;
  readBuffer: Uint8Array;
};

type ViewImage = { key: string; url: string };

export type InspirationPreviewRenderRequest = {
  inspiration: DetailedInspiration;
  viewId: number;
  mappings: Mapping[];
  maxDimension: number;
  signal?: AbortSignal;
};

export type InspirationPreviewRenderer = {
  render: (request: InspirationPreviewRenderRequest) => Promise<Blob>;
  dispose: () => void;
};

const PREVIEW_SCENE_CACHE_LIMIT = 32;
const PREVIEW_MATERIAL_CACHE_LIMIT = 8;

let state: State | null = null;
let scene: SceneTextures | null = null;

const LAYERS = [
  'layer_diffuse',
  'layer_lighting',
  'layer_global_illumination',
  'layer_detail',
] as const;

const hexToSrgb = (hex: string): [number, number, number] => {
  const [r, g, b] = hexToRgb(hex);
  return [r / 255, g / 255, b / 255];
};

const FULLSCREEN_PASS_VS = `#version 300 es
in vec2 a_position;
uniform int u_flipY;
out vec2 v_uv;
void main() {
  vec2 uv = a_position * 0.5 + 0.5;
  v_uv = u_flipY == 1 ? vec2(uv.x, 1.0 - uv.y) : uv;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

const BLIT_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
out vec4 outColor;
void main() {
  outColor = vec4(texture(u_texture, v_uv).rgb, 0.0);
}`;

const MATERIAL_FS = `#version 300 es
precision highp float;
#define MAX_SEGMENTS ${MAX_GRID_SEGMENTS}
in vec2 v_uv;
uniform vec2 u_imageSize;
uniform sampler2D u_mask;
uniform int u_isTexture;
uniform vec3 u_color;
uniform sampler2D u_material;
uniform int u_segmentCount;
uniform mat3 u_matrices[MAX_SEGMENTS];
uniform vec2 u_segmentCorners[MAX_SEGMENTS * 4]; // TL, TR, BR, BL per segment
uniform vec3 u_cornerLines[MAX_SEGMENTS - 1]; // line equation, > 0 right of the line
uniform vec2 u_sourceQuadSize;
uniform vec2 u_scaledTextureSize;
uniform float u_rotation;
out vec4 outColor;

vec3 srgbToLinear(vec3 c) {
  vec3 lo = c / 12.92;
  vec3 hi = pow((c + 0.055) / 1.055, vec3(2.4));
  return mix(lo, hi, step(vec3(0.04045), c));
}
${INVERSE_BILINEAR_GLSL}
// Linear V so neighboring segments line up along their shared corner line.
float getLinearV(vec2 p, int segment, float estimate) {
  float v1;
  float v2;
  if (!bilinearVRoots(
    p,
    u_segmentCorners[segment * 4],
    u_segmentCorners[segment * 4 + 1],
    u_segmentCorners[segment * 4 + 2],
    u_segmentCorners[segment * 4 + 3],
    v1,
    v2
  )) {
    return estimate;
  }

  // pick the root closest to the estimate, works outside the segment too
  return abs(v1 - estimate) < abs(v2 - estimate) ? v1 : v2;
}

void main() {
  float m = texture(u_mask, v_uv).r;
  if (m < 0.5) discard;

  if (u_isTexture == 0) {
    outColor = vec4(srgbToLinear(u_color), m);
    return;
  }

  vec2 v_pixel = v_uv * u_imageSize;

  int segment = 0;
  for (int i = 0; i < u_segmentCount - 1; i++) {
    if (dot(u_cornerLines[i], vec3(v_pixel, 1.0)) > 0.0) segment++;
  }

  vec3 projected = u_matrices[segment] * vec3(v_pixel, 1.0);
  float srcX = projected.x / projected.z;
  float srcY = projected.y / projected.z;

  if (u_segmentCount > 1) {
    float halfH = u_sourceQuadSize.y * 0.5;
    float estimate = (srcY + halfH) / u_sourceQuadSize.y;
    srcY = mix(-halfH, halfH, getLinearV(v_pixel, segment, estimate));
  }

  float angle = -radians(u_rotation);
  float cx = u_sourceQuadSize.x * 0.5;
  float cy = u_sourceQuadSize.y * 0.5;
  float tx = srcX - cx;
  float ty = srcY - cy;
  float c = cos(angle);
  float s = sin(angle);
  float rx = tx * c - ty * s;
  float ry = tx * s + ty * c;
  float fx = rx + cx;
  float fy = ry + cy;

  vec2 uv = vec2(fx, fy) / u_scaledTextureSize;
  vec4 texColor = texture(u_material, uv);
  outColor = vec4(srgbToLinear(texColor.rgb), m);
}`;

const COMPOSITE_FS = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_diffuse;
uniform sampler2D u_lighting;
uniform sampler2D u_gi;
uniform sampler2D u_detail;
uniform int u_has_gray;
uniform sampler2D u_gray;
out vec4 outColor;

vec3 linearToSrgb(vec3 c) {
  vec3 lo = c * 12.92;
  vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
  return mix(lo, hi, step(vec3(0.0031308), c));
}

void main() {
  vec4 diffuseSample = texture(u_diffuse, v_uv);
  vec3 d = diffuseSample.rgb;
  float edited = diffuseSample.a;
  vec3 l = texture(u_lighting, v_uv).rgb;
  vec3 g = texture(u_gi, v_uv).rgb;
  vec3 dt = texture(u_detail, v_uv).rgb;

  vec3 result;
  if (u_has_gray == 1 && edited > 0.5) {
    vec3 linearGray = texture(u_gray, v_uv).rgb;

    float grayAlbedoLinear = 0.214; 

    vec3 pureLighting = linearGray / grayAlbedoLinear;
    result = d * pureLighting;
  } else {
    result = d * (l + g) + dt;
  }

  outColor = vec4(linearToSrgb(max(result, vec3(0.0))), 1.0);
}`;

const createImageTexture = (
  gl: WebGL2RenderingContext,
  image: TexImageSource,
  internalFormat: number,
  filter: number,
) =>
  twgl.createTexture(gl, {
    src: image,
    internalFormat,
    format: gl.RGBA,
    type: gl.UNSIGNED_BYTE,
    wrap: gl.CLAMP_TO_EDGE,
    min: filter,
    mag: filter,
  });

const isRendererReady = (): boolean => {
  const sc = scene;
  return !!state && !!sc && LAYERS.every((id) => sc.layerTextures.has(id));
};

const deleteSceneTextures = (
  gl: WebGL2RenderingContext,
  sc: SceneTextures,
): void => {
  sc.layerTextures.forEach((texture) => gl.deleteTexture(texture));
  sc.maskTextures.forEach((texture) => gl.deleteTexture(texture));
};

const clearScene = (): void => {
  if (state && scene) deleteSceneTextures(state.gl, scene);
  scene = null;
};

const createState = (): State => {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const gl = canvas.getContext('webgl2', {
    premultipliedAlpha: false,
    antialias: false,
    preserveDrawingBuffer: true,
  });
  if (!gl) throw new Error('WebGL2 is not supported');

  gl.disable(gl.BLEND);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.SCISSOR_TEST);

  const blitProgram = twgl.createProgramInfo(gl, [FULLSCREEN_PASS_VS, BLIT_FS]);
  const materialProgram = twgl.createProgramInfo(gl, [
    FULLSCREEN_PASS_VS,
    MATERIAL_FS,
  ]);
  const compositeProgram = twgl.createProgramInfo(gl, [
    FULLSCREEN_PASS_VS,
    COMPOSITE_FS,
  ]);

  const fullScreenBuffer = twgl.createBufferInfoFromArrays(gl, {
    a_position: {
      numComponents: 2,
      data: new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    },
  });

  return {
    gl,
    canvas,
    materialCache: new Map(),
    diffuseFramebuffer: gl.createFramebuffer()!,
    diffuseTexture: gl.createTexture()!,
    blitProgram,
    materialProgram,
    compositeProgram,
    fullScreenBuffer,
    readBuffer: new Uint8Array(0),
  };
};

const ensureContext = (): State => (state ??= createState());

const loadMaskData = (image: HTMLImageElement): Uint8ClampedArray => {
  const cv = document.createElement('canvas');
  cv.width = image.naturalWidth;
  cv.height = image.naturalHeight;
  const ctx = cv.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(image, 0, 0);
  return ctx.getImageData(0, 0, cv.width, cv.height).data;
};

const getScaledDimensions = (
  width: number,
  height: number,
  maxDimension = Infinity,
): { width: number; height: number } => {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const getTextureSource = (
  image: HTMLImageElement,
  maxDimension?: number,
): TexImageSource => {
  const { width, height } = getScaledDimensions(
    image.naturalWidth,
    image.naturalHeight,
    maxDimension,
  );
  if (width === image.naturalWidth && height === image.naturalHeight) {
    return image;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(image, 0, 0, width, height);
  return canvas;
};

const ensureMaterialTexture = (
  s: State,
  url: string,
  maxDimension?: number,
): Promise<MaterialEntry | null> => {
  const key = maxDimension ? `${maxDimension}:${url}` : url;
  const cached = s.materialCache.get(key);
  if (cached) {
    s.materialCache.delete(key);
    s.materialCache.set(key, cached);
    return Promise.resolve(cached);
  }

  const promise: Promise<MaterialEntry | null> = ensureLoadedImage(
    url,
    'anonymous',
  )
    .then((image) => {
      if (s.materialCache.get(key) !== promise) return null;

      const tex = twgl.createTexture(s.gl, {
        src: getTextureSource(image, maxDimension),
        internalFormat: s.gl.RGBA8,
        format: s.gl.RGBA,
        type: s.gl.UNSIGNED_BYTE,
        wrap: s.gl.REPEAT,
        min: s.gl.LINEAR_MIPMAP_LINEAR,
        mag: s.gl.LINEAR,
      });

      const entry: MaterialEntry = {
        texture: tex,
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
      s.materialCache.set(key, entry);
      return entry;
    })
    .catch((err) => {
      console.error('[webglRenderer] material load failed', url, err);
      if (s.materialCache.get(key) === promise) s.materialCache.delete(key);
      return null;
    });

  s.materialCache.set(key, promise);
  return promise;
};

const resizeIfNeeded = (s: State, width: number, height: number): void => {
  if (s.canvas.width === width && s.canvas.height === height) return;
  s.canvas.width = width;
  s.canvas.height = height;

  const gl = s.gl;
  gl.bindTexture(gl.TEXTURE_2D, s.diffuseTexture);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA8,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  gl.bindFramebuffer(gl.FRAMEBUFFER, s.diffuseFramebuffer);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    s.diffuseTexture,
    0,
  );
};

const loadImages = (images: ViewImage[]) =>
  Promise.all(
    images.map(async ({ key, url }) => ({
      key,
      image: await ensureLoadedImage(url, 'anonymous'),
    })),
  );

const createSceneTextures = (
  gl: WebGL2RenderingContext,
  loaded: { key: string; image: HTMLImageElement }[],
  maxDimension?: number,
): SceneTextures | null => {
  if (loaded.length === 0) return null;

  const reference = loaded[0].image;
  const sc: SceneTextures = {
    ...getScaledDimensions(
      reference.naturalWidth,
      reference.naturalHeight,
      maxDimension,
    ),
    sourceWidth: reference.naturalWidth,
    sourceHeight: reference.naturalHeight,
    layerTextures: new Map(),
    maskTextures: new Map(),
    maskData: new Map(),
  };

  for (const { key, image } of loaded) {
    const source = getTextureSource(image, maxDimension);
    if (key.startsWith('layer')) {
      sc.layerTextures.set(
        key,
        createImageTexture(gl, source, gl.SRGB8_ALPHA8, gl.LINEAR),
      );
    } else {
      sc.maskTextures.set(
        key,
        createImageTexture(gl, source, gl.RGBA8, gl.LINEAR),
      );
      if (!maxDimension) sc.maskData.set(key, loadMaskData(image));
    }
  }

  return sc;
};

export const initializeRenderer = async (
  images: ViewImage[],
): Promise<void> => {
  const s = ensureContext();
  clearScene();
  scene = createSceneTextures(s.gl, await loadImages(images));
};

export const isRendererInitialized = (): boolean => isRendererReady();

export const getCanvasSize = (): { width: number; height: number } | null => {
  if (!isRendererReady()) return null;
  return { width: scene!.width, height: scene!.height };
};

export const preloadMaterialTexture = (url: string): void => {
  if (!isRendererReady()) return;
  void ensureMaterialTexture(state!, url);
};

export const resetRenderer = (): void => {
  if (!state) return;
  const s = state;
  clearScene();
  s.materialCache.forEach((m) => {
    if (!(m instanceof Promise)) s.gl.deleteTexture(m.texture);
  });
  s.materialCache.clear();
};

export const areCoordsInMask = (key: string, x: number, y: number): boolean => {
  if (!isRendererReady()) return false;
  const { width, height, maskData } = scene!;
  if (x < 0 || y < 0 || x >= width || y >= height) return false;
  const data = maskData.get(key);
  if (!data) return false;
  return data[(y * width + x) * 4] > 128;
};

type SegmentTransform = {
  segmentCount: number;
  matrices: Float32Array;
  segmentCorners: Float32Array;
  cornerLines: Float32Array;
};

type ResolvedMapping = {
  mask: WebGLTexture;
  color: [number, number, number];
  material?: MaterialEntry;
  segments?: SegmentTransform;
  sourceQuadSize?: [number, number];
  scaledTextureSize?: [number, number];
  rotation?: number;
};

const getCornerLineEquation = (
  [top, bottom]: [Point, Point],
  rightPoint: Point,
): [number, number, number] => {
  const a = top[1] - bottom[1];
  const b = bottom[0] - top[0];
  const c = -(a * top[0] + b * top[1]);
  const sign = a * rightPoint[0] + b * rightPoint[1] + c >= 0 ? 1 : -1;
  return [a * sign, b * sign, c * sign];
};

// Homography coefficients to a column-major mat3.
// prettier-ignore
const toMat3 = (h: number[]): number[] => [
  h[0], h[3], h[6],
  h[1], h[4], h[7],
  h[2], h[5], 1,
];

const buildSceneTransform = (
  textureWidth: number,
  textureHeight: number,
  realTextureWidth: number,
  realTextureHeight: number,
  realObjectWidth: number,
  realObjectHeight: number,
  corners: Point[],
  cornerLines?: Point[][],
): {
  segments: SegmentTransform;
  sourceQuadSize: [number, number];
  scaledTextureSize: [number, number];
} | null => {
  if (
    realObjectWidth === 0 ||
    realObjectHeight === 0 ||
    realTextureWidth === 0 ||
    realTextureHeight === 0 ||
    textureWidth === 0 ||
    textureHeight === 0
  ) {
    return null;
  }

  const scaledTextureWidth = Math.min(
    16384,
    Math.max(1, Math.round(textureWidth)),
  );
  const scaledTextureHeight = Math.min(
    16384,
    Math.max(1, Math.round(textureHeight)),
  );

  const sourceQuadWidth =
    (realObjectWidth / realTextureWidth) * scaledTextureWidth;
  const sourceQuadHeight =
    (realObjectHeight / realTextureHeight) * scaledTextureHeight;

  const halfW = sourceQuadWidth / 2;
  const halfH = sourceQuadHeight / 2;

  const projectionSegments = buildProjectionSegments(
    corners,
    cornerLines,
    -halfW,
    sourceQuadWidth,
    MAX_GRID_SEGMENTS,
  );

  const segments: SegmentTransform = {
    segmentCount: projectionSegments.length,
    matrices: new Float32Array(MAX_GRID_SEGMENTS * 9),
    segmentCorners: new Float32Array(MAX_GRID_SEGMENTS * 8),
    cornerLines: new Float32Array((MAX_GRID_SEGMENTS - 1) * 3),
  };

  for (let i = 0; i < projectionSegments.length; i++) {
    const { quad, u0, u1 } = projectionSegments[i];
    const [tl, tr, br, bl] = quad;

    const transform = getPerspectiveTransform(
      tl[0],
      tl[1],
      tr[0],
      tr[1],
      br[0],
      br[1],
      bl[0],
      bl[1],
      u0,
      -halfH,
      u1,
      -halfH,
      u1,
      halfH,
      u0,
      halfH,
    );
    if (!transform) return null;

    segments.matrices.set(toMat3(transform), i * 9);
    segments.segmentCorners.set([tl, tr, br, bl].flat(), i * 8);
    if (i > 0) {
      segments.cornerLines.set(
        getCornerLineEquation([tl, bl], corners[1]),
        (i - 1) * 3,
      );
    }
  }

  return {
    segments,
    sourceQuadSize: [sourceQuadWidth, sourceQuadHeight],
    scaledTextureSize: [scaledTextureWidth, scaledTextureHeight],
  };
};

const resolveMappings = async (
  s: State,
  sc: SceneTextures,
  mappings: Mapping[],
  previewMaxDimension?: number,
): Promise<ResolvedMapping[]> => {
  const results = await Promise.all(
    mappings.map(async (mapping): Promise<ResolvedMapping | null> => {
      const mask = sc.maskTextures.get(mapping.mask);
      if (!mask) return null;

      if (!mapping.is_texture_material || !mapping.material) {
        return { mask, color: hexToSrgb(mapping.material ?? '#000000') };
      }

      const material = await ensureMaterialTexture(
        s,
        mapping.material,
        previewMaxDimension,
      );
      if (!material) {
        if (previewMaxDimension) {
          throw new Error(
            `[webglRenderer] texture unavailable: ${mapping.material}`,
          );
        }
        console.warn('[webglRenderer] texture unavailable', mapping);
        return null;
      }

      const transform = buildSceneTransform(
        material.width,
        material.height,
        mapping.real_texture_width,
        mapping.real_texture_height,
        mapping.object_width,
        mapping.object_height,
        mapping.corners,
        mapping.cornerLines,
      );
      if (!transform) {
        console.warn(
          '[webglRenderer] no geometry for this component — corners/object size are 0',
          mapping,
        );
        return null;
      }

      return {
        mask,
        color: [0, 0, 0],
        material,
        segments: transform.segments,
        sourceQuadSize: transform.sourceQuadSize,
        scaledTextureSize: transform.scaledTextureSize,
        rotation: mapping.rotation,
      };
    }),
  );

  return results.filter((r): r is ResolvedMapping => r !== null);
};

const drawScene = (
  s: State,
  sc: SceneTextures,
  resolved: ResolvedMapping[],
  flipY: boolean,
): void => {
  const gl = s.gl;
  const { width, height } = sc;
  resizeIfNeeded(s, width, height);

  const layers = LAYERS.map((id) => {
    const tex = sc.layerTextures.get(id);
    if (!tex) throw new Error(`[webglRenderer] missing required layer ${id}`);
    return tex;
  });
  const [diffuseLayer, lightingLayer, giLayer, detailLayer] = layers;
  const grayLayer = sc.layerTextures.get('layer_gray_rendering');

  gl.viewport(0, 0, width, height);

  gl.bindFramebuffer(gl.FRAMEBUFFER, s.diffuseFramebuffer);
  gl.useProgram(s.blitProgram.program);
  twgl.setBuffersAndAttributes(gl, s.blitProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.blitProgram, { u_texture: diffuseLayer });
  twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);

  gl.useProgram(s.materialProgram.program);
  twgl.setBuffersAndAttributes(gl, s.materialProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.materialProgram, {
    u_imageSize: [sc.sourceWidth, sc.sourceHeight],
  });

  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(
    gl.SRC_ALPHA,
    gl.ONE_MINUS_SRC_ALPHA,
    gl.ONE,
    gl.ONE_MINUS_SRC_ALPHA,
  );

  for (const mapping of resolved) {
    const isTexture = !!mapping.material;
    twgl.setUniforms(s.materialProgram, {
      u_mask: mapping.mask,
      u_isTexture: isTexture ? 1 : 0,
      u_color: mapping.color,
      u_material: mapping.material?.texture,
      ...(mapping.segments && {
        u_segmentCount: mapping.segments.segmentCount,
        u_matrices: mapping.segments.matrices,
        u_segmentCorners: mapping.segments.segmentCorners,
        u_cornerLines: mapping.segments.cornerLines,
      }),
      u_sourceQuadSize: mapping.sourceQuadSize ?? [1, 1],
      u_scaledTextureSize: mapping.scaledTextureSize ?? [1, 1],
      u_rotation: mapping.rotation ?? 0,
    });
    twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);
  }

  gl.disable(gl.BLEND);

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.useProgram(s.compositeProgram.program);
  twgl.setBuffersAndAttributes(gl, s.compositeProgram, s.fullScreenBuffer);
  twgl.setUniforms(s.compositeProgram, {
    u_flipY: flipY ? 1 : 0,
    u_diffuse: s.diffuseTexture,
    u_lighting: lightingLayer,
    u_gi: giLayer,
    u_detail: detailLayer,
    u_has_gray: grayLayer ? 1 : 0,
    u_gray: grayLayer ?? s.diffuseTexture,
  });
  twgl.drawBufferInfo(gl, s.fullScreenBuffer, gl.TRIANGLE_STRIP);
};

export const render = async (mappings: Mapping[]): Promise<Uint8Array> => {
  if (!isRendererReady()) {
    throw new Error('[webglRenderer] renderer not initialized');
  }
  const s = state!;
  const sc = scene!;
  const resolved = await resolveMappings(s, sc, mappings);
  if (scene !== sc)
    throw new Error('[webglRenderer] scene changed during render');

  drawScene(s, sc, resolved, false);

  const { width, height } = sc;
  if (s.readBuffer.length !== width * height * 4) {
    s.readBuffer = new Uint8Array(width * height * 4);
  }
  s.gl.readPixels(
    0,
    0,
    width,
    height,
    s.gl.RGBA,
    s.gl.UNSIGNED_BYTE,
    s.readBuffer,
  );
  return s.readBuffer;
};

const getViewImages = (
  inspiration: DetailedInspiration,
  selectorState: SelectorState,
  viewId: number,
): ViewImage[] | null => {
  const currentView = getCurrentView(selectorState, viewId);
  if (!currentView) return null;

  const componentIdsInView = new Set(
    getVisibleComponentIds(selectorState, viewId),
  );
  const masks = Object.entries(inspiration.render.masks)
    .filter(([id]) => componentIdsInView.has(id))
    .map(([id, value]) => ({
      key: `mask_${id}`,
      url: value.replace('{view}', String(currentView.id)),
    }));
  const viewLayerKeys = new Set(currentView.layers ?? []);

  const layers = Object.entries(inspiration.render.layers)
    .filter(
      ([key]) => LAYERS.includes(`layer_${key}`) || viewLayerKeys.has(key),
    )
    .map(([key, value]) => ({
      key: `layer_${key}`,
      url: value.replace('{view}', String(currentView.id)),
    }));

  return [...masks, ...layers];
};

export const loadViewAssets = async (
  inspiration: DetailedInspiration,
  selectorState: SelectorState,
  viewId: number,
): Promise<boolean> => {
  const images = getViewImages(inspiration, selectorState, viewId);
  if (!images) return false;

  if (isRendererInitialized()) resetRenderer();

  await initializeRenderer(images);

  return isRendererInitialized();
};

export const createInspirationPreviewRenderer =
  (): InspirationPreviewRenderer => {
    let s: State | undefined;
    let disposed = false;
    let queue: Promise<unknown> = Promise.resolve();
    const scenes = new Map<string, SceneTextures>();

    const renderPreview = async ({
      inspiration,
      viewId,
      mappings,
      maxDimension,
      signal,
    }: InspirationPreviewRenderRequest): Promise<Blob> => {
      const checkpoint = () => {
        signal?.throwIfAborted();
        if (disposed) throw new Error('[webglRenderer] renderer disposed');
      };
      checkpoint();
      s ??= createState();
      const gl = s.gl;

      const images = getViewImages(
        inspiration,
        {
          inspiration,
          currentViewId: viewId,
          componentOverwrites: {},
          editMasterData: false,
        },
        viewId,
      );
      if (!images)
        throw new Error('[webglRenderer] inspiration view not found');

      const sceneKey = JSON.stringify([maxDimension, images]);
      let sc = scenes.get(sceneKey);
      scenes.delete(sceneKey);
      if (!sc) {
        const loaded = await loadImages(images);
        checkpoint();
        sc = createSceneTextures(gl, loaded, maxDimension)!;
      }
      scenes.set(sceneKey, sc);
      if (scenes.size > PREVIEW_SCENE_CACHE_LIMIT) {
        const [oldestKey, oldest] = scenes.entries().next().value!;
        scenes.delete(oldestKey);
        deleteSceneTextures(gl, oldest);
      }

      for (const [key, entry] of s.materialCache) {
        if (s.materialCache.size <= PREVIEW_MATERIAL_CACHE_LIMIT) break;
        if (entry instanceof Promise) continue;
        gl.deleteTexture(entry.texture);
        s.materialCache.delete(key);
      }

      const resolved = await resolveMappings(s, sc, mappings, maxDimension);
      checkpoint();
      drawScene(s, sc, resolved, true);

      return new Promise<Blob>((resolve, reject) =>
        s!.canvas.toBlob(
          (blob) =>
            blob
              ? resolve(blob)
              : reject(new Error('[webglRenderer] image encoding failed')),
          'image/png',
        ),
      );
    };

    return {
      render: (request) => {
        const result = queue.then(() => renderPreview(request));
        queue = result.catch(() => undefined);
        return result;
      },
      dispose: () => {
        disposed = true;
        s?.gl.getExtension('WEBGL_lose_context')?.loseContext();
      },
    };
  };
